/**
 * 10. If AHU has two tunnels with different airflow_direction_id:
 *    - Determine the tunnel with the shorter total length
 *    - Automatically increase the length of the auto-created plenum(s)
 *      in that tunnel as needed to ensure both tunnels have equal length
 * If tunnel_configuration is 1:2 tunnels width must be equal
 */

import { ahuApi, openingApi } from '../../../../services';
import { dispatchMutation, dispatchQuery } from '../../../../utils/dispatch.utils';
import { createAccessDoor } from '../../designAccessories/createAccessDoor';
import { roundBasedOnArguments } from '../../designAHU.utils';
import { PLENUM_LENGTH } from '../../../data/bot';
import {
  DEFAULT_COEFFICIENTS,
  IBotData,
  IBotSection,
  ISection,
  ITunnel,
} from '../../../../models';

function sumLength(sections: ISection[]): number {
  return sections.reduce((sum, s) => sum + (s.length ?? 0), 0);
}

async function applyPlenum(
  sections: ISection[],
  index: number,
  diff: number,
  minWidth: number,
  botSection: IBotSection,
  getOptions: () => { signal?: AbortSignal },
) {
  if (diff <= 0) return;

  const target = sections[index];

  if (target?.type === 'plenum') {
    await dispatchMutation(
      ahuApi.endpoints.updateSection,
      {
        id: target.id,
        body: {
          length: roundBasedOnArguments(
            (target.length ?? PLENUM_LENGTH) + diff,
            target.increment || 1,
          ),
        },
      },
      getOptions(),
    );
  } else {
    const increment = sections[0].increment;
    const thickness = sections[0].casing_insulation_thick;
    const opening = botSection.accessories.find((e) => e.type === 'opening');
    const access_door = botSection.accessories.find(
      (e) => e.type === 'access_door',
    );
    const plenum: ISection = await dispatchMutation(
      ahuApi.endpoints.createSection,
      {
        body: {
          type: 'plenum',
          length: roundBasedOnArguments(diff + (target.auto_adjust && target.type !== 'energy_wheel' ? thickness : 0), increment),
          tunnel_id: target.tunnel_id,
          order_rate: !index ? index + 1 : index + 2,
        },
      },
      getOptions(),
    );
    if (opening) {
      await dispatchMutation(
        openingApi.endpoints.updateOpening,
        {
          id: opening.model.id,
          body: {
            section_id: plenum.id,
          },
        },
        getOptions(),
      );
    }
    if (access_door) {
      createAccessDoor(
        { type: 'plenum', model: plenum },
        plenum,
        access_door,
        //@ts-ignore
        { getOptions },
        minWidth,
      );
    }
  }
}

export async function step10AlignTunnelsLengthAndWidth(
  botData: IBotData,
  ahuId: number,
  tunnels: ITunnel[],
  minWidth: number,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
  checkStopped?: () => boolean,
): Promise<ITunnel[]> {
  /**
   * Width alignment for 1:2 configuration
   */
  if (botData.tunnels[1] && botData.tunnels[1].tunnel_index === '1:2') {
    const widths = tunnels.map((t) => t.width);
    const updateTunnels = [tunnels[0], tunnels[1]];

    if (widths[0] !== widths[1]) {
      const sourceIndex = widths[0] > widths[1] ? 0 : 1;
      const targetIndex = sourceIndex === 0 ? 1 : 0;

      const updatedTunnel: ITunnel = await dispatchMutation(
        ahuApi.endpoints.updateAhuTunnel,
        {
          id: tunnels[targetIndex].id,
          body: { width: tunnels[sourceIndex].width },
        },
        getOptions(),
      );

      updateTunnels[targetIndex] = updatedTunnel;
      onProgress?.(DEFAULT_COEFFICIENTS.increase_tunnel_width);
    }

    if (botData.tunnels_have_same_directions) return updateTunnels;
  }

  if (botData.tunnels_have_same_directions) return tunnels;
  if (checkStopped?.()) {
    return tunnels;
  }
  /**
   * Tunnel configuration 4 & 5 (energy wheel split)
   */
  if (
    botData.tunnel_configuration === 4 ||
    botData.tunnel_configuration === 5
  ) {
    const tunnelsSections = await Promise.all(
      tunnels.map(
        (t) =>
          dispatchQuery(
            ahuApi.endpoints.getTunnelSections,
            t.id,
            getOptions(),
          ) as Promise<ISection[]>,
      ),
    );

    const tunnelParts = tunnelsSections.map((sections, i) => {
      const airflow_direction_id = tunnels[i].airflow_direction_id;
      const energyWheelIndex = sections.findIndex(
        (s) => s.type === 'energy_wheel',
      );

      const before =
        energyWheelIndex === -1
          ? sections
          : sections.slice(0, energyWheelIndex);

      const after =
        energyWheelIndex === -1 ? [] : sections.slice(energyWheelIndex + 1);

      return {
        before,
        after,
        beforeLength:
          airflow_direction_id === 1 ? sumLength(before) : sumLength(after),
        afterLength:
          airflow_direction_id === 1 ? sumLength(after) : sumLength(before),
      };
    });

    const maxBefore = Math.max(...tunnelParts.map((t) => t.beforeLength));
    const maxAfter = Math.max(...tunnelParts.map((t) => t.afterLength));

    for (let i = 0; i < tunnels.length; i++) {
      const tunnel = tunnels[i];
      const sections = tunnelsSections[i];
      const { beforeLength, afterLength } = tunnelParts[i];

      const beforeDiff = maxBefore - beforeLength;
      const afterDiff = maxAfter - afterLength;

      const firstIndex = 0;
      const lastIndex = sections.length - 1;
      if (checkStopped?.()) {
        return tunnels;
      }
      if (tunnel.airflow_direction_id === 1) {
        // BEFORE → first
        if (beforeDiff > 0) {
          await applyPlenum(
            sections,
            firstIndex,
            beforeDiff,
            minWidth,
            botData.tunnels[i].sections[0],
            getOptions,
          );
        }

        // AFTER → last
        if (afterDiff > 0) {
          await applyPlenum(
            sections,
            lastIndex,
            afterDiff,
            minWidth,
            botData.tunnels[i].sections[lastIndex],
            getOptions,
          );
        }
      }

      if (tunnel.airflow_direction_id === 2) {
        // BEFORE → last
        if (beforeDiff > 0) {
          await applyPlenum(
            sections,
            lastIndex,
            beforeDiff,
            minWidth,
            botData.tunnels[i].sections[lastIndex],
            getOptions,
          );
        }

        // AFTER → first
        if (afterDiff > 0) {
          await applyPlenum(
            sections,
            firstIndex,
            afterDiff,
            minWidth,
            botData.tunnels[i].sections[0],
            getOptions,
          );
        }
      }
    }

    onProgress?.(DEFAULT_COEFFICIENTS.increase_tunnel_length);
    return await dispatchQuery(
      ahuApi.endpoints.getAhuTunnels,
      ahuId,
      getOptions(),
    );
  }

  /**
   * Legacy fallback (total tunnel length)
   */
  const tunnelsResult: ITunnel[] = await dispatchQuery(
    ahuApi.endpoints.getAhuTunnels,
    ahuId,
    getOptions(),
  );

  const lengths = tunnelsResult.map((t) => t.length);

  if (lengths[0] !== lengths[1]) {
    const longer = lengths[0] > lengths[1] ? 0 : 1;
    const shorter = longer === 0 ? 1 : 0;
    const diff = Math.abs(lengths[0] - lengths[1]);

    const plenum = botData.tunnels[shorter].plenum as IBotSection;

    const resp: ISection = await dispatchMutation(
      ahuApi.endpoints.updateSection,
      {
        id: plenum.model?.id,
        body: {
          length: roundBasedOnArguments(
            (plenum.model?.length || PLENUM_LENGTH) + diff,
            plenum.model?.increment || 1,
          ),
        },
      },
      getOptions(),
    );

    plenum.model = resp;
  }

  onProgress?.(DEFAULT_COEFFICIENTS.increase_tunnel_length);
  return tunnelsResult;
}
